import { addLocale } from "primereact/api";
import type { LocaleOptions } from "primereact/api";

import {
  CALENDAR_LOCALE_FIGMA_OPTIONS,
  CALENDAR_LOCALE_OPTIONS,
  DATE_PICKER_LOCALE,
  DATE_PICKER_LOCALE_FIGMA,
} from "../components/calendar/locale";

/**
 * Locale pt-BR do PrimeReact 10.9.8 (todas as chaves de `LocaleOptions`). Os nomes de dia/mês vêm de
 * `CALENDAR_LOCALE_OPTIONS` (fonte única do calendário); o resto, dos textos que os wrappers do DS já usavam.
 */
export const PRIME_LOCALE_PT_BR: LocaleOptions = {
  ...CALENDAR_LOCALE_OPTIONS,
  startsWith: "Começa com",
  contains: "Contém",
  notContains: "Não contém",
  endsWith: "Termina com",
  equals: "Igual a",
  notEquals: "Diferente de",
  noFilter: "Sem filtro",
  filter: "Filtrar",
  lt: "Menor que",
  lte: "Menor ou igual a",
  gt: "Maior que",
  gte: "Maior ou igual a",
  dateIs: "Data é",
  dateIsNot: "Data não é",
  dateBefore: "Data anterior a",
  dateAfter: "Data posterior a",
  custom: "Personalizado",
  apply: "Aplicar",
  matchAll: "Corresponder a todos",
  matchAny: "Corresponder a qualquer",
  addRule: "Adicionar regra",
  removeRule: "Remover regra",
  accept: "Sim",
  reject: "Não",
  choose: "Escolher",
  upload: "Enviar",
  cancel: "Cancelar",
  close: "Fechar",
  pending: "Pendente",
  fileSizeTypes: ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
  chooseYear: "Escolher o ano",
  chooseMonth: "Escolher o mês",
  prevDecade: "Década anterior",
  nextDecade: "Próxima década",
  prevYear: "Ano anterior",
  nextYear: "Próximo ano",
  prevMonth: "Mês anterior",
  nextMonth: "Próximo mês",
  prevHour: "Hora anterior",
  nextHour: "Próxima hora",
  prevMinute: "Minuto anterior",
  nextMinute: "Próximo minuto",
  prevSecond: "Segundo anterior",
  nextSecond: "Próximo segundo",
  am: "AM",
  pm: "PM",
  now: "Agora",
  dateFormat: "dd/mm/yy",
  weak: "Fraca",
  medium: "Média",
  strong: "Forte",
  passwordPrompt: "Digite uma senha",
  emptyFilterMessage: "Nenhuma opção encontrada.",
  emptyMessage: "Nenhuma opção disponível.",
  aria: {
    trueLabel: "Verdadeiro",
    falseLabel: "Falso",
    nullLabel: "Não selecionado",
    star: "1 estrela",
    stars: "{star} estrelas",
    selectAll: "Todos os itens selecionados",
    unselectAll: "Todos os itens desmarcados",
    close: "Fechar",
    previous: "Anterior",
    next: "Próximo",
    navigation: "Navegação",
    scrollTop: "Rolar para o topo",
    moveTop: "Mover para o topo",
    moveUp: "Mover para cima",
    moveDown: "Mover para baixo",
    moveBottom: "Mover para o fim",
    moveToTarget: "Mover para o destino",
    moveToSource: "Mover para a origem",
    moveAllToTarget: "Mover todos para o destino",
    moveAllToSource: "Mover todos para a origem",
    pageLabel: "Página {page}",
    firstPageLabel: "Primeira página",
    lastPageLabel: "Última página",
    nextPageLabel: "Próxima página",
    prevPageLabel: "Página anterior",
    rowsPerPageLabel: "Linhas por página",
    jumpToPageDropdownLabel: "Ir para a página",
    jumpToPageInputLabel: "Ir para a página",
    selectRow: "Linha selecionada",
    unselectRow: "Linha desmarcada",
    expandRow: "Linha expandida",
    collapseRow: "Linha recolhida",
    showFilterMenu: "Mostrar o menu de filtro",
    hideFilterMenu: "Ocultar o menu de filtro",
    filterOperator: "Operador do filtro",
    filterConstraint: "Restrição do filtro",
    editRow: "Editar linha",
    saveEdit: "Salvar edição",
    cancelEdit: "Cancelar edição",
    listView: "Visualização em lista",
    gridView: "Visualização em grade",
    slide: "Slide",
    slideNumber: "{slideNumber}",
    zoomImage: "Ampliar imagem",
    zoomIn: "Aumentar o zoom",
    zoomOut: "Diminuir o zoom",
    rotateRight: "Girar para a direita",
    rotateLeft: "Girar para a esquerda",
    listLabel: "Lista de opções",
  },
};

/**
 * Locale global: passe em `PrimeReactProvider value={{ locale: PRIME_LOCALE }}` (o Provider reaplica o `locale` a cada
 * montagem, então `locale()` da API sozinho não basta).
 */
export const PRIME_LOCALE = DATE_PICKER_LOCALE;

/**
 * Registra o pt-BR e a variante `figma` do calendário (dias "Dom Seg Ter", mês curto). `addLocale` substitui o locale
 * inteiro pelo nome, então este é o único lugar que o chama; repetir a chamada é inofensivo.
 */
export function registerPrimeLocales(): void {
  addLocale(DATE_PICKER_LOCALE, PRIME_LOCALE_PT_BR);
  addLocale(DATE_PICKER_LOCALE_FIGMA, { ...PRIME_LOCALE_PT_BR, ...CALENDAR_LOCALE_FIGMA_OPTIONS });
}

registerPrimeLocales();
